/**
 * Software encode is never an acceptable end state.
 *
 * The host used to renegotiate once and then latch `accepted-software`, streaming behind a
 * warning banner for the rest of the session. Two things were wrong with that: the
 * "renegotiate" re-applied identical codec preferences, so by construction it produced the
 * same encoder; and a software-encoded session is high-CPU and high-latency in a way that
 * reads to players as a network fault, so tolerating it silently is worse than refusing.
 *
 * This controller now drives a bounded escalation — each step excludes the H.264 profile
 * observed encoding in software and re-offers on the rest — and reports `refuse` once the
 * escalations are exhausted. The caller is expected to end the session at that point.
 */

import { SOFTWARE_ENCODE_POLICY, classifyImplementationName, type SoftwareEncodePolicy } from './webrtcLatencyContracts';

export type SoftwareEncodeRecoveryState =
  /** No software encode observed, or a previous escalation reached hardware. */
  | 'idle'
  /** Software observed; a hardware renegotiate is in flight. */
  | 'escalating'
  /** Escalation exhausted — the session must not continue. */
  | 'refused';

export type SoftwareEncodeAction =
  /** Nothing to do. */
  | 'none'
  /** Exclude the current profile and renegotiate toward hardware. */
  | 'escalate'
  /** Hardware is unreachable on this host; end the session. */
  | 'refuse';

export class SoftwareEncodeRecoveryController {
  private state: SoftwareEncodeRecoveryState = 'idle';
  private escalations = 0;

  constructor(private readonly policy: SoftwareEncodePolicy = SOFTWARE_ENCODE_POLICY) {}

  /**
   * Feed the outbound `encoderImplementation` from getStats.
   *
   * @returns what the caller must do now. `refuse` is returned exactly once per peer, so a
   * repeating stats poll cannot fire repeated disconnects for the same failure.
   */
  onOutboundEncoderImplementation(impl: string | undefined): SoftwareEncodeAction {
    const kind = classifyImplementationName(impl);

    if (kind !== 'software') {
      // `unknown` is not evidence of software — an unrecognised implementation string must
      // not end a session. Only a positive software classification escalates.
      // Clear both `escalating` and `refused` when hardware encode appears — the encoder
      // has recovered and the session should no longer report a stuck terminal state.
      if (this.state === 'escalating' || this.state === 'refused') this.state = 'idle';
      return 'none';
    }

    if (this.state === 'refused') return 'none';

    if (this.escalations < this.policy.maxHardwareEscalations) {
      this.escalations += 1;
      this.state = 'escalating';
      return 'escalate';
    }

    this.state = 'refused';
    return 'refuse';
  }

  getState(): SoftwareEncodeRecoveryState {
    return this.state;
  }

  getEscalationCount(): number {
    return this.escalations;
  }
}
